"use client";

import {
  CopyButton,
  Notice,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { CARD, WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import { CurveChart } from "@/components/tools/astronomy/curve-chart";
import type { SunAngleStrings } from "@/components/tools/sun-angle-calculator";
import { useToolNumber } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  clockWithDay,
  hoursAndMinutes,
  longDay,
} from "@/lib/tool-kit/astronomy/format";
import {
  COMPASS_POINTS,
  compassPoint,
  elevationSeries,
  julianDay,
  shadowRatio,
  solarDayOnLocalDate,
  sunPosition,
  twilightPhase,
} from "@/lib/tool-kit/astronomy/solar";
import {
  localDayNumber,
  parseIsoDay,
  zonedToInstant,
} from "@/lib/tool-kit/astronomy/zone";

interface Props {
  form: { place: string; zone: string; date: string; time: string };
  instant: number;
  latitude: number;
  longitude: number;
  locale: string;
  strings: SunAngleStrings;
}

const TWO_DECIMALS = { minimumFractionDigits: 2, maximumFractionDigits: 2 };

function pathTicks(
  low: number,
  high: number
): { value: number; label: string }[] {
  const step = high - low > 90 ? 30 : 15;
  const ticks: { value: number; label: string }[] = [];
  for (let value = low; value <= high; value += step) {
    ticks.push({ value, label: `${value}°` });
  }
  return ticks;
}

function DayChart({ props, start }: { props: Props; start: number }) {
  const { strings, latitude, longitude, instant } = props;
  const series = elevationSeries(latitude, longitude, start, 15, 96).map(
    ([minutes, elevation]) => [minutes / 60, elevation] as const
  );
  const values = series.map(([, y]) => y);
  const low = Math.min(-15, Math.floor(Math.min(...values) / 15) * 15);
  const high = Math.max(15, Math.ceil(Math.max(...values) / 15) * 15);
  const hours = (instant - start) / 3_600_000;
  const current = sunPosition(latitude, longitude, instant).elevation;
  return (
    <CurveChart
      description={strings.chartDescription}
      id="sa-chart"
      label={strings.chartLabel}
      marker={hours >= 0 && hours <= 24 ? { x: hours, y: current } : null}
      points={series}
      xDomain={[0, 24]}
      xTicks={[0, 3, 6, 9, 12, 15, 18, 21, 24].map((value) => ({
        value,
        label: String(value),
      }))}
      xTitle={strings.chartX}
      yDomain={[low, high]}
      yTicks={pathTicks(low, high)}
      yTitle={strings.chartY}
      zeroLine
    />
  );
}

export function SunAngleResults(props: Props) {
  const { form, instant, latitude, longitude, locale, strings } = props;
  const fmt = useToolNumber();
  const two = (value: number) => fmt(value, TWO_DECIMALS);
  const pos = sunPosition(latitude, longitude, instant);
  const localDay = localDayNumber(instant, form.zone);
  const sun = solarDayOnLocalDate(latitude, longitude, localDay, (ms) =>
    localDayNumber(ms, form.zone)
  );
  const compass = (strings.compass as readonly string[])[
    COMPASS_POINTS.indexOf(compassPoint(pos.azimuth))
  ];
  const ratio = shadowRatio(pos.elevation);
  const day = parseIsoDay(form.date);
  const start =
    day === null
      ? instant
      : zonedToInstant({ ...day, hour: 0, minute: 0, second: 0 }, form.zone);
  const clock = (ms: number) =>
    clockWithDay(locale, ms, form.zone, localDay, strings);
  const duration = hoursAndMinutes(sun.daylightSeconds);
  const place =
    (strings.placeNames as Record<string, string>)[form.place] ??
    `${fmt(latitude)}, ${fmt(longitude)}`;
  const summary = t(strings.summary, {
    place,
    date: form.date,
    time: form.time,
    zone: form.zone,
    elevation: two(pos.elevation),
    azimuth: two(pos.azimuth),
    compass,
  });
  const utcDate = new Date(instant);
  const utcText = `${utcDate.toISOString().slice(0, 16).replace("T", " ")}`;
  return (
    <>
      <section className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        <StatGrid>
          <StatCard
            emphasis
            label={strings.elevation}
            value={`${two(pos.elevation)}°`}
          />
          <StatCard
            emphasis
            label={strings.azimuth}
            value={`${two(pos.azimuth)}° ${compass}`}
          />
          <StatCard label={strings.zenith} value={`${two(pos.zenith)}°`} />
          <StatCard
            label={strings.geometric}
            value={`${two(pos.geometricElevation)}°`}
          />
          <StatCard
            label={strings.declination}
            value={`${two(pos.declination)}°`}
          />
          <StatCard
            label={strings.shadow}
            value={
              ratio === null
                ? strings.noShadow
                : t(strings.metres, {
                    value: fmt(ratio, { maximumFractionDigits: 2 }),
                  })
            }
          />
        </StatGrid>
        <p className="text-foreground text-sm">
          {strings.phase[twilightPhase(pos.geometricElevation)]}
        </p>
        <CopyButton
          copiedLabel={strings.copied}
          label={strings.copy}
          text={summary}
        />
      </section>
      <section className={`${CARD} space-y-4`}>
        <h2 className={WIDGET_H2}>
          {t(strings.dayHeading, { date: longDay(locale, localDay) })}
        </h2>
        <StatGrid>
          <StatCard
            label={strings.sunrise}
            value={sun.sunrise === null ? "—" : clock(sun.sunrise)}
          />
          <StatCard label={strings.solarNoon} value={clock(sun.noon)} />
          <StatCard
            label={strings.sunset}
            value={sun.sunset === null ? "—" : clock(sun.sunset)}
          />
          <StatCard
            label={strings.noonElevation}
            value={`${two(sun.noonElevation)}°`}
          />
          <StatCard
            label={strings.daylight}
            value={t(strings.duration, {
              hours: fmt(duration.hours),
              minutes: fmt(duration.minutes),
            })}
          />
          <StatCard
            label={strings.equationOfTime}
            value={`${fmt(pos.equationOfTime, { maximumFractionDigits: 1 })} min`}
          />
        </StatGrid>
        {sun.status === "polar-day" ? (
          <Notice>{strings.polarDay}</Notice>
        ) : null}
        {sun.status === "polar-night" ? (
          <Notice>{strings.polarNight}</Notice>
        ) : null}
        <h3 className={WIDGET_H3}>{strings.chartHeading}</h3>
        <DayChart props={props} start={start} />
      </section>
      <section className={`${CARD} space-y-2`}>
        <h3 className={WIDGET_H3}>{strings.workingHeading}</h3>
        <ul className="space-y-2 text-foreground text-sm leading-relaxed">
          <li>
            {t(strings.workingJulian, {
              jd: fmt(julianDay(instant), { maximumFractionDigits: 4 }),
              utc: utcText,
              declination: two(pos.declination),
              eot: fmt(pos.equationOfTime, { maximumFractionDigits: 2 }),
            })}
          </li>
          <li>
            {t(strings.workingSolarTime, {
              tst: `${fmt(Math.floor(pos.trueSolarTime / 60))}:${String(Math.floor(pos.trueSolarTime % 60)).padStart(2, "0")}`,
              ha: two(pos.hourAngle),
            })}
          </li>
          <li>
            {t(strings.workingElevation, {
              lat: fmt(latitude, { maximumFractionDigits: 4 }),
              geo: two(pos.geometricElevation),
              refraction: fmt(pos.refraction, { maximumFractionDigits: 3 }),
              elevation: two(pos.elevation),
            })}
          </li>
        </ul>
      </section>
    </>
  );
}
